<template>
  <viewer-layer
    v-if="isFavoritesEnabled"
    v-show="layerVisible"
    class="bounce-in"
    :class="{ 'bounce-in-active': favorites.dialog.visible }"
    :close-event="() => showFavoritesDialog(false)"
    inner-class-name="viewer-layer-second-inner fullscreen"
  >
    <div class="favorites h-100">
      <div class="header">
        <div class="title">
          {{ $t('favorites.title') }}
        </div>
        <div class="tabs">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            class="tab"
            :class="{ active: tab.key === activeTab }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </div>
        </div>
        <div class="search">
          <el-input
            v-model="searchKeyword"
            prefix-icon="el-icon-search"
            size="medium"
            clearable
            @input="getSortedData"
          />
        </div>
        <div class="export">
          <el-button
            type="primary"
            icon="el-icon-download"
            size="medium"
            :loading="pdf.loading"
            @click="exportPdf"
          >
            {{ pdf.active ? $t('favorites.pdf.exporting') : $t('favorites.pdf.export') }}
          </el-button>
        </div>
      </div>
      <template v-if="favorites.inited">
        <div class="list">
          <table>
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.key"
                  :width="column.width"
                  :class="{ clickable: column.sort !== false }"
                  @click="sortBy(column)"
                >
                  {{ column.text }}
                  <a
                    v-if="column.sort !== false"
                    class="sorting"
                    :class="{
                      active: activeSort(column.key),
                      asc: activeSort(column.key) && isSortAsc()
                    }"
                  >
                    <i class="isif isif-arrow-down" />
                  </a>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="sortedFavoriteData.length">
                <template v-for="favorite in sortedFavoriteData">
                  <tr :key="'main-' + favorite.id">
                    <td
                      v-for="column in columns"
                      :key="column.key"
                      :class="{ center: column.center }"
                      :style="{ width: column.width }"
                    >
                      <template v-if="column.key === 'image'">
                        <a
                          v-if="activeTab === 'company'"
                          class="item-image-link"
                          @click="activeSubRow = activeSubRow === favorite.id ? '' : favorite.id"
                        >
                          <img
                            v-if="favorite.image"
                            class="item-image"
                            :src="favorite.image"
                          >
                          <default-logo
                            v-else
                            class="item-image"
                          />
                        </a>
                        <a
                          v-else-if="favorite.image"
                          class="item-image-link"
                          @click="goto(favorite)"
                        >
                          <img
                            class="item-image"
                            :src="favorite.image"
                          >
                        </a>
                      </template>
                      <template v-else-if="column.key === 'remove'">
                        <a
                          class="item-remove"
                          :title="$t('favorites.remove')"
                          @click="removeFavorite(favorite)"
                        >
                          <i class="isif isif-trash" />
                        </a>
                      </template>
                      <template v-else-if="column.key === 'note'">
                        <note
                          :favorite="favorite"
                          @update="updateFavorite"
                        />
                      </template>
                      <template v-else>
                        <i-text-collapse
                          :text="favorite[column.key] || '-'"
                        />
                      </template>
                    </td>
                  </tr>
                  <tr
                    v-if="activeTab === 'company'"
                    v-show="activeSubRow === favorite.id"
                    :key="'sub-' + favorite.id"
                    class="sub-row"
                  >
                    <td
                      :colspan="columns.length"
                    >
                      <div class="panoramas">
                        <div
                          v-for="panorama in favorite.panoramas"
                          :key="panorama.id"
                          class="panorama"
                          @click="goto(panorama)"
                        >
                          <div
                            class="thumb"
                            :style="{ backgroundImage: `url('${panorama.image}')` }"
                          />
                          <div class="name">
                            {{ panorama.name }}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
              <template v-else>
                <tr class="empty">
                  <td :colspan="columns.length">
                    {{ $t('favorites.table.listEmpty') }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
      <template v-else>
        <div class="loading">
          <img src="@/assets/images/loading.svg">
        </div>
      </template>
    </div>
    <!-- PDF will be auto generated once display -->
    <pdf
      v-if="pdf.active"
      :active-tab="activeTab"
      :category="category"
      :data="favoriteData"
      @export="pdfExported"
    />
  </viewer-layer>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import ViewerLayer from '@/components/common/ViewerLayer/index'
import ITextCollapse from '@/components/ITextCollapse'
import { getMarkerProfile } from '@/api/helpers'
import Sorting from '@/utils/sorting'
import DefaultLogo from './DefaultLogo'
import Note from './Note'
import Pdf from './Pdf'

const sorting = new Sorting('type')

export default {
  name: 'FavoritesDialog',
  components: {
    ViewerLayer,
    ITextCollapse,
    DefaultLogo,
    Note,
    Pdf
  },
  props: {
    layerVisible: {
      type: Boolean,
      default: false
    },
    gotoTarget: {
      type: Function,
      default: () => {}
    }
  },
  data: self => ({
    activeTab: 'company',
    activeSubRow: '',
    searchKeyword: '',
    pdf: {
      loading: false,
      active: false
    },
    sortedFavoriteData: []
  }),
  computed: {
    ...mapState([
      'favorites'
    ]),
    ...mapGetters('favorites', {
      isFavoritesEnabled: 'enabled'
    }),
    tabs () {
      return [
        {
          key: 'company',
          label: this.$t('favorites.category.company'),
          columns: [
            {
              key: 'image',
              text: this.$t('favorites.table.image.company'),
              width: '20%',
              sort: false
            },
            {
              key: 'name',
              text: this.$t('favorites.table.name.company'),
              width: '25%'
            },
            // {
            //   key: 'price',
            //   text: this.$t('favorites.table.price'),
            //   width: '10%',
            //   sort: false
            // },
            {
              key: 'description',
              text: this.$t('favorites.table.description.company'),
              width: '25%',
              sort: false
            },
            {
              key: 'note',
              text: this.$t('favorites.table.note'),
              width: '25%',
              sort: false
            },
            {
              key: 'remove',
              text: '',
              width: '5%',
              center: true,
              sort: false
            }
          ]
        },
        // {
        //   key: 'panorama',
        //   label: this.$t('favorites.category.panorama')
        // },
        {
          key: 'marker',
          label: this.$t('favorites.category.marker'),
          columns: [
            {
              key: 'image',
              text: this.$t('favorites.table.image.marker'),
              width: '20%',
              sort: false
            },
            {
              key: 'name',
              text: this.$t('favorites.table.name.marker'),
              width: '25%'
            },
            // {
            //   key: 'price',
            //   text: this.$t('favorites.table.price'),
            //   width: '10%',
            //   sort: false
            // },
            {
              key: 'description',
              text: this.$t('favorites.table.description.marker'),
              width: '25%',
              sort: false
            },
            {
              key: 'note',
              text: this.$t('favorites.table.note'),
              width: '25%',
              sort: false
            },
            {
              key: 'remove',
              text: '',
              width: '5%',
              center: true,
              sort: false
            }
          ]
        }
        // {
        //   key: 'card',
        //   label: this.$t('favorites.category.card'),
        //   columns: [
        //     {
        //       key: 'image',
        //       text: this.$t('favorites.table.image.card'),
        //       width: '15%',
        //       sort: false
        //     },
        //     {
        //       key: 'name',
        //       text: this.$t('favorites.table.name.card'),
        //       width: '15%'
        //     },
        //     {
        //       key: 'company',
        //       text: this.$t('favorites.table.company'),
        //       width: '20%'
        //     },
        //     {
        //       key: 'occupation',
        //       text: this.$t('favorites.table.occupation'),
        //       width: '20%',
        //       sort: false
        //     },
        //     {
        //       key: 'note',
        //       text: this.$t('favorites.table.note'),
        //       width: '25%',
        //       sort: false
        //     },
        //     {
        //       key: 'remove',
        //       text: '',
        //       width: '5%',
        //       center: true,
        //       sort: false
        //     }
        //   ]
        // }
      ]
    },
    category () {
      // For favorite category
      const categories = {
        company: 'user',
        marker: 'marker',
        card: 'nameCard'
      }
      return categories[this.activeTab] || ''
    },
    columns () {
      const tab = this.tabs.find(t => t.key === this.activeTab)
      return tab ? tab.columns : []
    },
    favoriteData () {
      const category = this.category
      const data = this.favorites.data[category + 's'] || []
      return data.map(item => {
        if (category === 'user') {
          const companyInfo = item.companyInfo || {}

          return {
            id: item.id,
            favoriteId: item.favoriteId,
            name: companyInfo.company || 'Unknown',
            description: companyInfo.description || '',
            image: companyInfo.logoUrl || '',
            note: item.note || '',
            panoramas: item.panoramas
              ? item.panoramas.map(panorama => {
                return {
                  id: panorama.objectId,
                  favoriteId: panorama.favoriteId,
                  buildingId: panorama.buildingId,
                  name: this.getPanoramaI18nName(panorama),
                  image: panorama.thumbnail,
                  index: panorama.index + 1
                }
              })
              : []
          }
        }
        if (category === 'marker') {
          const data = {
            id: item.id,
            favoriteId: item.favoriteId,
            buildingId: item.buildingId,
            type: item.type,
            name: '',
            description: '',
            image: '',
            price: this.getMarkerI18nValue(item, 'price'),
            panoramaId: item.currentPanoramaId,
            note: item.note || ''
          }

          if (data.type === 'multitag') {
            data.name = this.getMultiTagField(item, 'name')
            data.description = this.getMultiTagField(item, 'description')
            data.image =
              this.getMultiTagField(item, 'images')[0] ||
              require('@/components/common/Krpano/img/markers/multitag.png')
          } else {
            data.name = this.getMarkerI18nValue(item, 'name')
            data.description = this.getMarkerI18nValue(item, 'description')
            item.images = item.photo ? item.photo.split(',') : []
            data.image = getMarkerProfile(item)
          }
          if (data.type === 'videoplay') {
            data.description = ''
          }

          return data
        }
        if (category === 'nameCard') {
          return {}
        }
        return item
      })
    }
  },
  watch: {
    activeTab () {
      this.activeSubRow = ''
      this.searchKeyword = ''
      this.getSortedData()
    },
    favoriteData: {
      handler (bool) {
        if (bool) {
          this.getSortedData()
        }
      },
      immediate: true
    },
    'favorites.dialog.visible' (bool) {
      if (!bool) {
        this.activeTab = 'company'
        this.activeSubRow = ''
      }
    }
  },
  methods: {
    ...mapActions('favorites', {
      showFavoritesDialog: 'showDialog',
      remove: 'remove',
      update: 'update'
    }),
    getPanoramaI18nName (panorama) {
      const locale = this.$i18n.locale
      const caption = panorama.customCategory || this.$t(panorama.category)
      if (panorama.categoryMulti) {
        const categoryMulti = JSON.parse(panorama.categoryMulti)
        if (categoryMulti[locale] === undefined) {
          return caption
        } else {
          return categoryMulti[locale]
        }
      }
      return caption
    },
    getMarkerI18nValue (marker, field) {
      const locale = this.$i18n.locale
      const i18nField = marker[field + 'Multi']
      if (i18nField && i18nField[locale]) {
        return i18nField[locale]
      }
      return marker[field]
    },
    getMultiTagField (marker, field) {
      if (Array.isArray(marker.multitagInfo)) {
        const availableItems = marker.multitagInfo.filter(tag => !tag.hidden)
        const item = availableItems.find(item => item.mainPhoto) || availableItems[0]
        if (item) {
          item.images = item.photo ? item.photo.split(',') : []
          return item[field]
        }
      }
      return ''
    },
    removeFavorite (item) {
      this.remove({
        category: this.category,
        id: item.id
      })
    },
    updateFavorite (item) {
      this.update({
        favoriteId: item.favoriteId,
        payload: {
          note: item.note
        }
      })
      this.getSortedData()
    },
    goto (favorite) {
      const isMarker = this.category === 'marker'
      const payload = {
        buildingId: favorite.buildingId
      }
      if (isMarker) {
        payload.panoramaId = favorite.panoramaId
        payload.markerId = favorite.id
      } else {
        payload.panoramaId = favorite.id
        payload.panoramaIndex = favorite.index
      }
      this.gotoTarget(payload, isMarker)
    },
    getSortedData () {
      const keyword = this.searchKeyword.toLowerCase()
      const data = this.favoriteData
        .filter(data =>
          this.searchKeyword === '' ||
          data.name.toLowerCase().includes(keyword) ||
          (data.description || '').toLowerCase().includes(keyword) ||
          (data.note || '').toLowerCase().includes(keyword)
        )
      this.sortedFavoriteData = sorting.sort(data, this.$i18n.locale)
    },
    activeSort (key) {
      return sorting.key === key
    },
    isSortAsc () {
      return sorting.order === sorting.ORDER.ASC
    },
    sortBy (column) {
      if (column.sort === false) {
        return
      }
      sorting.sortBy(column.key)
      this.getSortedData()
    },
    async exportPdf () {
      if (this.pdf.loading) {
        return
      }
      this.pdf.loading = true
      await this.$nextTick()
      this.pdf.active = true
    },
    pdfExported () {
      this.pdf.active = false
      this.pdf.loading = false
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~css/common/main'

.favorites {
  display: flex
  flex-direction: column

  *,
  /deep/ * {
    text-shadow: none
  }
}
.header {
  display: flex;
  align-items: center
  padding: 0 60px 20px
  border-bottom: 2px solid rgba(255,255,255,0.8)

  .title {
    flex: 0 0 auto
    margin-right: 80px
    font-size: 26px
    font-weight: bold
  }
  .tabs {
    flex: 1
    display: flex

    .tab {
      font-size: 18px
      background: $dusty-gray
      line-height: 1
      padding: 8px 16px
      border-radius: 5px
      cursor: pointer

      &:not(:last-child) {
        margin-right: 15px
      }
      &.active {
        background: $pink-color
      }
    }
  }
  .search {

    /deep/ .el-input__icon {
      color: $pink-color
    }
  }
  .export {
    margin-left: 10px

    i {
      font-size: 20px
    }
    &.active {
      cursor: default
    }
  }
}
.loading {
  display: flex
  justify-content: center
}
.list {
  flex: 1
  overflow: hidden

  table {
    display: flex
    flex-direction: column
    width: 100%
    height: 100%
  }
  tr:not(.sub-row):not(:first-child) {
    border-top: 1px solid rgba(#101010, .15)
  }
  thead,
  tbody tr {
    width: 100%
    display: table
    table-layout: fixed
  }
  thead {
    tr {
      border-top: none
      border-bottom: 2px solid rgba(#CCC, .25)
    }
  }
  tbody {
    display: block
    overflow: auto
    overflow: overlay
  }
  .sub-row {

    td {
      background: #FFF
      color: $vrl-primary-text
      padding-top: 30px
      padding-bottom: 30px
    }
    .panoramas {
      display: grid
      grid-gap: 10px
      grid-template-columns: repeat(auto-fill, 200px)
    }
    .panorama {
      padding: 8px 10px
      cursor: pointer

      &:hover {
        background: rgba(#CCC, .75)
      }
    }
    .thumb {
      width: 100%
      padding-bottom: 56.25%
      background-size: cover
      background-position: center
    }
    .name {
      line-height: 1
      text-align: center
      margin-top: 6px
    }
  }
  th, td {
    padding-left: 10px
    padding-right: 10px
    text-align: left

    &.center {
      text-align: center
    }
  }
  th {
    padding-top: 20px
    padding-bottom: 15px
    font-size: 20px
  }
  td {
    padding-top: 10px
    padding-bottom: 10px
    line-height: 22px
    font-size: 16px
    vertical-align: middle
    word-break: break-word
  }
  tr th, tr td {
    &:first-child {
      padding-left: 60px
    }
    &:last-child {
      padding-right: 60px
    }
  }
  .empty td {
    padding: 30px 0
    text-align: center
  }
  span {
    text-shadow: none !important
  }
}
.item-image-link {
  vertical-align: middle
  display: inline-block
  cursor: pointer
}
.item-image {
  width: auto
  height: auto
  max-width: 100%
  max-height: 100px
  display: block
}
.item-remove {
  font-size: 20px
  color: #bcbdc1
  display: inline-block
  padding: 5px
  cursor: pointer
}
.clickable {
  cursor: pointer
}
.sorting {
  display: inline-block
  width: 20px
  height: 20px
  text-align: center
  font-size: 16px
  color: rgba(#BBB, .75)
  cursor: pointer
  text-shadow: none

  &:hover {
    color: #DDD !important
  }
  &.active {
    color: $pink-color !important
  }
  i {
    transition: .2s transform
  }
  &.asc i {
    transform: rotate(180deg)
  }
}
</style>
